import Helpers from "./helpers";
import Logger from "./Logger";

declare var $: any;

enum FileUploadStatus {
	Available,
	HandlingFiles,
	RemovingFiles
}

export default class FileUpload {

	private dropArea: HTMLElement;
	private dropAreaInput: HTMLInputElement;
	private currentFileList: FileList | null;
	private dataTransferConstructorSupported = false;
	private status = FileUploadStatus.Available;

	constructor(dropArea: HTMLElement) {
		this.dropArea = dropArea;
		this.dropAreaInput = this.dropArea.querySelector('.js-file-drop-input') as HTMLInputElement;
		this.testForDataTransferSupport();
		if (this.dataTransferConstructorSupported) {
			this.attachEvents();
			this.dropArea.classList.add('is-supported');
		}
	}

	private testForDataTransferSupport() {
		try {
			new DataTransfer();
			this.dataTransferConstructorSupported = true;
		} catch(error) {
			Logger.trackException(error);
		}
	}

	private attachEvents() {

		// Prevent default drag behaviors
		['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
			this.dropArea.addEventListener(eventName, this.preventDefaults, false);
			document.body.addEventListener(eventName, this.preventDefaults, false);
		});

		// Highlight drop area when item is dragged over it
		['dragenter', 'dragover'].forEach(eventName => {
			this.dropArea.addEventListener(eventName, ()=>this.highlight(), false);
		});

		['dragleave', 'drop'].forEach(eventName => {
			this.dropArea.addEventListener(eventName, () => this.unhighlight(), false);
		});

		this.dropArea.addEventListener('drop', (e) =>this.handleDrop(e), false);

		this.dropAreaInput.addEventListener('input', ()=>this.handleFiles(this.dropAreaInput.files), false);
	}

	private preventDefaults(e) {
		e.preventDefault();
		e.stopPropagation();
	}

	private highlight() {
		this.dropArea.classList.add('active');
	}

	private unhighlight() {
		this.dropArea.classList.remove('active');
	}

	private handleDrop(e) {
		const dt = e.dataTransfer;
		const files = dt.files;
		this.handleFiles(files);
	}

	private handleFiles(files) {
		if (this.status !== FileUploadStatus.Available) {
			return false;
		}
		this.status = FileUploadStatus.HandlingFiles;
		const copyOfFiles = [...files] as unknown as FileList;
		this.mergeFilesWithPreviouslyUploaded(copyOfFiles);
		for (let index = 0; index < copyOfFiles.length; index++) {
			const file = copyOfFiles[index];
			this.previewFile(file);
		}
		this.status = FileUploadStatus.Available;
	}

	private mergeFilesWithPreviouslyUploaded(newFiles: FileList) {
		if (!this.dataTransferConstructorSupported) {
			// If no DataTransfer constructor we can't merge, so we'll just take the last uploaded files
			Helpers.empty(document.querySelector('.js-file-drop-thumbnails'));
			this.currentFileList = null;
			return;
		}
		let list = new DataTransfer();
		const allFiles = this.mergeFilelists(this.currentFileList, newFiles);
		for (let index = 0; index < allFiles.length; index++) {
			const file = allFiles[index] as File;
			list.items.add(file);
		}
		this.dropAreaInput.files = list.files;
		this.currentFileList = this.dropAreaInput.files;
	}

	private mergeFilelists(firstFileList, secondFileList) {
		if (!firstFileList) {
			firstFileList = [];
		}
		// @ts-ignore
		let firstFileListArray = Array.from(firstFileList);
		// @ts-ignore
		const secondFileListArray = Array.from(secondFileList);
		for (let i = 0; i < secondFileListArray.length; i++) {
			firstFileListArray.push(secondFileListArray[i]);
		}
		return firstFileListArray;
	}

	private previewFile(file) {
		let reader = new FileReader();
		const self = this;
		reader.readAsDataURL(file);
		reader.onloadend = () => {

			let previewImage = "";
			if(file.type === "application/pdf") {
				previewImage = "data:image/png;base64,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";
			} else {
				previewImage = reader.result as string;
			}

			let thumbnailContainer = document.createElement('div');
			thumbnailContainer.classList.add('c-thumbnails__thumbnail');

			let img = document.createElement('img');
			img.src = previewImage;
			img.setAttribute('alt', '');
			img.classList.add('c-thumbnails__image');

			let removeButton = document.createElement('button');
			removeButton.setAttribute('type', 'button');
			removeButton.classList.add('c-thumbnails__remove');
			removeButton.insertAdjacentHTML("beforeend", `
				<svg viewBox="0 0 24 24" class="c-thumbnails__remove-icon" width="32" height="32">
					<path fill="currentColor" d="M18.984 6.422l-5.578 5.578 5.578 5.578-1.406 1.406-5.578-5.578-5.578 5.578-1.406-1.406 5.578-5.578-5.578-5.578 1.406-1.406 5.578 5.578 5.578-5.578z"></path>
				</svg>
				<span class="u-visuallyhidden">Remove file</span>
			`);
			removeButton.addEventListener('click', ()=> self.removeFileFromFileList(file, thumbnailContainer as HTMLElement));

			thumbnailContainer.appendChild(img);
			thumbnailContainer.appendChild(removeButton);

			const thumbnailsContainer = document.querySelector('.js-file-drop-thumbnails');
			if(thumbnailsContainer) {
				thumbnailsContainer.appendChild(thumbnailContainer);
			}
		}
	}

	private removeFileFromFileList(fileToRemove: File, thumbnailElement: HTMLElement) {
		this.status = FileUploadStatus.RemovingFiles;
		if (!this.dataTransferConstructorSupported) {
			// If no DataTransfer constructor we can't update the files on the input, so we'll remove all
			Helpers.empty(document.querySelector('.js-file-drop-thumbnails'));
			this.dropAreaInput.value = "";
			this.dropAreaInput.files = null;
			this.currentFileList = null;
			this.status = FileUploadStatus.Available;
			return;
		}

		const dt = new DataTransfer();
		if (!this.dropAreaInput.files) {
			this.status = FileUploadStatus.Available;
			return;
		}
		for (let i = 0; i < this.dropAreaInput.files.length; i++) {
			const file = this.dropAreaInput.files[i]
			if (file.name !== fileToRemove.name) {
				dt.items.add(file);
			}
		}
		this.dropAreaInput.files = dt.files;
		Helpers.removeElement(thumbnailElement);
		this.currentFileList = this.dropAreaInput.files;
		$(this.dropAreaInput).trigger('change');
		this.status = FileUploadStatus.Available;
	}

}
